import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, ImageIcon, Loader2, RefreshCw, RotateCcw, Wand2 } from "lucide-react";
import {
  approveDialogueArtwork,
  getDialogueArtwork,
  regenerateDialogueArtwork,
} from "../../../Services/adminResearchLibrary";
import ConfirmDialog from "../../../Components/admin/shared/ConfirmDialog";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Checkbox } from "../../../ui/checkbox";
import { Textarea } from "../../../ui/textarea";
import { errorMessage, formatDateTime } from "./researchLibraryFormat";
import { CropStrip, VariantPicker, assetLabel, isVariantKind } from "./ArtworkVariants";

// Member surfaces read the 16:9 banner.
export const ARTWORK_KIND = "banner";
export const ARTWORK_POLL_MS = 5000;
const PROMPT_MIN = 20;
const PROMPT_MAX = 4000;

const QA_VARIANTS = { pass: "success", reject: "destructive", error: "warning" };

export const assetOf = (artwork, kind) =>
  (artwork?.assets || []).find((asset) => asset?.kind === kind) || null;

export const bannerAsset = (artwork) => assetOf(artwork, ARTWORK_KIND);

/** The prompt a regeneration would use: saved override, else the library's. */
export const initialPrompt = (asset) =>
  asset?.prompt_override ?? asset?.default_prompt ?? asset?.live?.prompt ?? "";

const ErrorBox = ({ children }) => (
  <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
    {children}
  </div>
);

const UsedPrompt = ({ prompt }) =>
  prompt ? (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted-foreground">Last used prompt</summary>
      <p className="mt-1 whitespace-pre-wrap rounded-md bg-muted/40 p-2">{prompt}</p>
    </details>
  ) : null;

const Frame = ({ url, alt, emptyText, square = false }) => (
  <div
    className={`${square ? "aspect-square" : "aspect-video"} w-full overflow-hidden rounded-lg border bg-muted/40`}
  >
    {url ? (
      <img src={url} alt={alt} className="h-full w-full object-cover" />
    ) : (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-slate-100 to-slate-300 p-4 text-center text-sm text-muted-foreground">
        <ImageIcon className="h-6 w-6" />
        {emptyText}
      </div>
    )}
  </div>
);

const LiveImage = ({ live, name, square }) => (
  <div className="space-y-2">
    <h3 className="text-sm font-semibold">Live</h3>
    <Frame
      url={live?.url}
      alt={`Live ${name}`}
      square={square}
      emptyText={
        square
          ? "Not live yet — members see the banner"
          : "No approved image — members see the gradient"
      }
    />
    {live && (
      <p className="text-xs text-muted-foreground">
        v{live.version} · approved by {live.approved_by || "—"} ·{" "}
        {formatDateTime(live.approved_at)}
        {live.approval_source ? ` · ${live.approval_source}` : ""}
      </p>
    )}
    <CropStrip crops={live?.crops} title="Crops members see" />
    <UsedPrompt prompt={live?.prompt} />
  </div>
);

const CandidateImage = ({ candidate, disabled, onApprove, name, square }) => {
  if (!candidate) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Candidate</h3>
        <p className="text-sm text-muted-foreground">No candidate awaiting review.</p>
      </div>
    );
  }
  const qa = candidate.visual_qa;
  const failed = candidate.status === "failed";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">Candidate</h3>
        {qa?.verdict && (
          <Badge variant={QA_VARIANTS[qa.verdict] || "outline"}>QA: {qa.verdict}</Badge>
        )}
        {failed && <Badge variant="destructive">failed</Badge>}
      </div>
      <Frame
        url={failed ? null : candidate.url}
        alt={`Candidate ${name}`}
        square={square}
        emptyText="Render failed"
      />
      <p className="text-xs text-muted-foreground">
        v{candidate.version} · generated {formatDateTime(candidate.generated_at)}
      </p>
      {qa?.notes && <p className="text-xs">{qa.notes}</p>}
      {qa?.contains_text && (
        <p className="text-xs text-amber-700">
          QA found text in the image{qa.text_found ? `: “${qa.text_found}”` : ""}
        </p>
      )}
      {qa?.people_visible && (
        <p className="text-xs text-amber-700">QA found a person in a no-person image</p>
      )}
      <CropStrip crops={candidate.crops} title="Crops" />
      <UsedPrompt prompt={candidate.prompt} />
      {candidate.status === "pending_review" && (
        <Button size="sm" onClick={onApprove} disabled={disabled}>
          <CheckCircle2 className="mr-2 h-4 w-4" /> Approve v{candidate.version}
        </Button>
      )}
    </div>
  );
};

const DialogueArtworkPanel = ({ dialogueId }) => {
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [savePrompt, setSavePrompt] = useState(true);
  const [approveOpen, setApproveOpen] = useState(false);
  const [kind, setKind] = useState(ARTWORK_KIND);
  const promptDirty = useRef(false);
  const kindRef = useRef(ARTWORK_KIND);

  const asset = assetOf(artwork, kind);
  const regeneration = asset?.regeneration;
  const running = regeneration?.state === "running";
  // Keep polling while any image of this Dialogue is generating, not only the shown one.
  const anyRunning = (artwork?.assets || []).some((a) => a?.regeneration?.state === "running");
  const square = isVariantKind(kind);
  const name = kind === ARTWORK_KIND ? "banner" : assetLabel(asset) || kind;
  const hasAny = Boolean(asset?.live || asset?.candidate);

  // Prefill the editor from the server unless the admin is mid-edit.
  const applyArtwork = useCallback((next) => {
    setArtwork(next || null);
    if (!promptDirty.current) setPrompt(initialPrompt(assetOf(next, kindRef.current)));
  }, []);

  const select = (next) => {
    kindRef.current = next;
    promptDirty.current = false;
    setKind(next);
    setPrompt(initialPrompt(assetOf(artwork, next)));
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      applyArtwork(await getDialogueArtwork(dialogueId));
    } catch (err) {
      setError(errorMessage(err, "Failed to load the artwork"));
    } finally {
      setLoading(false);
    }
  }, [dialogueId, applyArtwork]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Chained poll: each fetched artwork schedules the next tick while running.
  useEffect(() => {
    if (!anyRunning) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const next = await getDialogueArtwork(dialogueId);
        if (!cancelled) applyArtwork(next);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Could not read the generation status"));
      }
    }, ARTWORK_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [anyRunning, artwork, dialogueId, applyArtwork]);

  const act = async (fn, fallback) => {
    setBusy(true);
    setError(null);
    try {
      applyArtwork(await fn());
    } catch (err) {
      setError(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const trimmed = prompt.trim();
  const promptInvalid =
    trimmed.length > 0 && (trimmed.length < PROMPT_MIN || trimmed.length > PROMPT_MAX);

  const regenerate = () =>
    act(async () => {
      const next = await regenerateDialogueArtwork(dialogueId, kind, {
        prompt: trimmed || undefined,
        save_prompt: savePrompt,
      });
      promptDirty.current = false;
      return next;
    }, "Failed to start a regeneration");

  const resetPrompt = () =>
    act(async () => {
      const next = await regenerateDialogueArtwork(dialogueId, kind, {
        reset_prompt: true,
      });
      promptDirty.current = false;
      return next;
    }, "Failed to reset the prompt");

  const approve = async () => {
    const version = asset?.candidate?.version;
    await act(
      () => approveDialogueArtwork(dialogueId, kind, version),
      "Failed to approve the image"
    );
  };

  const disabled = busy || running || loading;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg">Image</CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh image
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {artwork?.high_sensitivity && (
          <p className="text-sm text-amber-700">
            Sensitive dialogue — images always need manual approval.
          </p>
        )}
        {artwork && artwork.store_enabled === false && (
          <p className="text-sm text-muted-foreground">
            No image store is configured — regeneration is unavailable.
          </p>
        )}
        {error && <ErrorBox>{error}</ErrorBox>}
        {!loading && (artwork?.assets || []).length > 1 && (
          <VariantPicker assets={artwork.assets} selected={kind} onSelect={select} />
        )}
        {!loading && square && (
          <p className="text-xs text-muted-foreground">
            {assetLabel(asset)}
            {asset?.enhanced_review ? " · flagged for enhanced review by the release" : ""}
            {" · images that pass Visual QA go live automatically"}
          </p>
        )}
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading image…</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <LiveImage live={asset?.live} name={name} square={square} />
            <CandidateImage
              candidate={asset?.candidate}
              disabled={disabled}
              onApprove={() => setApproveOpen(true)}
              name={name}
              square={square}
            />
          </div>
        )}

        {running && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
            <Loader2 className="h-4 w-4 animate-spin" /> Generating… usually 1–3 minutes
          </div>
        )}
        {!running && regeneration?.state === "failed" && (
          <ErrorBox>Generation failed: {regeneration.error || "unknown error"}</ErrorBox>
        )}

        <div className="space-y-2">
          <label htmlFor="artwork-prompt" className="text-sm font-medium">
            Prompt
          </label>
          <Textarea
            id="artwork-prompt"
            rows={5}
            value={prompt}
            disabled={disabled}
            onChange={(e) => {
              promptDirty.current = true;
              setPrompt(e.target.value);
            }}
          />
          {promptInvalid && (
            <p className="text-xs text-destructive">
              The prompt must be {PROMPT_MIN}–{PROMPT_MAX} characters.
            </p>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id="artwork-save-prompt"
              checked={savePrompt}
              disabled={disabled}
              onCheckedChange={(value) => setSavePrompt(value === true)}
            />
            <label htmlFor="artwork-save-prompt" className="text-sm">
              Save as this dialogue&apos;s prompt
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={regenerate} disabled={disabled || promptInvalid}>
              <Wand2 className="mr-2 h-4 w-4" /> {!square || hasAny ? "Regenerate" : "Generate"}
            </Button>
            <Button variant="outline" onClick={resetPrompt} disabled={disabled}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset to library prompt
            </Button>
          </div>
        </div>
      </CardContent>

      <ConfirmDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        title={`Approve candidate v${asset?.candidate?.version ?? ""}?`}
        description="Members will see this image on the dialogue's cards and detail page within about a minute."
        confirmLabel="Approve"
        onConfirm={approve}
      />
    </Card>
  );
};

export default DialogueArtworkPanel;
